// Psychologist/PsychologistConsultationDateTimePicker.tsx
import React, {useEffect, useState} from "react";
import moment, {Moment} from "moment";
import {IConsultationDuration} from "../../Types/psychology/consultation";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import {useTheme} from "Theme/ThemeContext";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {axios} from "Auth/axiosConfig";
import pprint from '../../Utils/pprint';
import {FC, FCCC, FRCC, FRSC} from "WideLayout/Layouts";
import CircularProgress from "Core/components/elements/CircularProgress";

interface ConsultationSlot {
    start: string; // YYYY-MM-DDTHH:MM:SS±HH:MM
    end: string;
}

interface PsychologistConsultationDateTimePickerProps {
    className?: string;
    psychologistId: number;
    duration: IConsultationDuration;
    onConfirmSlot: (slot: string) => void;
}

const dayNames = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const PsychologistConsultationDateTimePicker: React.FC<PsychologistConsultationDateTimePickerProps> = (
    {
        className,
        psychologistId,
        duration,
        onConfirmSlot
    }) => {
    const [currentWeekStart, setCurrentWeekStart] = useState<Moment>(moment().startOf("isoWeek"));
    const [slots, setSlots] = useState<ConsultationSlot[]>([]);
    const [selectedDay, setSelectedDay] = useState<Moment>(moment().startOf("day"));
    const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
    const {byResponse} = useErrorProcessing();
    const {plt} = useTheme();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [noSlots, setNoSlots] = useState<boolean>(false);

    const fetchSlotsForMonth = async () => {
        setIsLoading(true);
        const nowPlus20Min = moment().add(20, 'minutes').format("YYYY-MM-DDTHH:mm:ssZ");
        const oneMonthLater = moment().add(1, 'months').add(20, 'minutes').format("YYYY-MM-DDTHH:mm:ssZ");

        try {
            const response = await axios.get(
                `/api/v1/psychologists/${psychologistId}/availability/`,
                {
                    params: {start: nowPlus20Min, end: oneMonthLater, duration: duration.id},
                }
            );
            pprint('fetchSlotsForMonth'); // Для отладки, можно удалить после
            pprint(response.data); // Для отладки, можно удалить после
            setIsLoading(false);
            return response.data as ConsultationSlot[];
        } catch (e) {
            byResponse(e);
            setIsLoading(false);
            return [];
        }
    };

    useEffect(() => {
        const loadSlots = async () => {
            const fetchedSlots = await fetchSlotsForMonth();

            // Фильтруем слоты, которые начинаются не раньше текущего времени + 20 минут
            const nowPlus20Min = moment().add(20, 'minutes');
            const filteredSlots = fetchedSlots.filter(slot => moment(slot.start).isAfter(nowPlus20Min));

            if (filteredSlots.length > 0) {
                // Сортируем слоты по дате
                filteredSlots.sort((a, b) => moment(a.start).diff(moment(b.start)));
                setSlots(filteredSlots);

                const earliestSlot = filteredSlots[0];
                const firstAvailableDay = moment(earliestSlot.start).startOf("day");
                setSelectedDay(firstAvailableDay);

                // Устанавливаем текущую неделю на неделю первого доступного слота
                setCurrentWeekStart(firstAvailableDay.clone().startOf("isoWeek"));

                setNoSlots(false);
            } else {
                setNoSlots(true);
                setSelectedDay(moment().startOf("day"));
                setSlots([]);
            }
        };

        loadSlots().then();
    }, [duration, psychologistId]);

    const handleDayChange = (day: Moment) => {
        setSelectedDay(day);
        setSelectedSlot(null);
    };

    const handleSlotSelect = (slot: string) => {
        setSelectedSlot(slot);
        onConfirmSlot(slot);
    };

    const getSlotsForSelectedDay = () => {
        if (!selectedDay) return [];
        return slots.filter((slot) =>
            moment(slot.start).isSame(selectedDay, "day")
        );
    };

    const renderDayButtons = () => {
        return [...Array(7)].map((_, i) => {
            const day = currentWeekStart.clone().add(i, "days");
            const isSelected = day.isSame(selectedDay, "day");
            const isBeforeToday = day.isBefore(moment().startOf("day"), "day");

            return (
                <button
                    key={i}
                    className={`rounded-top-3 ftrans-300-eio px-2 fs-6`}
                    style={{
                        color: plt.text.primary80,
                        border: "0",
                        borderBottom: isSelected
                            ? `1px solid ${plt.colors.secondary.light}`
                            : `1px solid ${plt.colors.secondary.lighter}`,
                        backgroundColor: isSelected
                            ? plt.colors.secondary.light
                            : plt.colors.secondary.lighter,
                        cursor: isBeforeToday ? "not-allowed" : "pointer",
                        opacity: isBeforeToday ? 0.5 : 1,
                    }}
                    onClick={() => {
                        if (!isBeforeToday) {
                            handleDayChange(day);
                        }
                    }}
                    disabled={isBeforeToday}
                >
                    <div style={{color: plt.text.primary60}}>
                        {dayNames[day.isoWeekday() - 1]}
                    </div>
                    <div style={{color: plt.text.primary60}}>{day.date()}</div>
                </button>
            );
        });
    };

    const renderSlots = () => {
        const daySlots = getSlotsForSelectedDay();

        if (noSlots && slots.length === 0) {
            return (
                <div className={`pt-2px`} style={{color: plt.text.primary40}}>
                    Слотов на ближайший месяц нет
                </div>
            );
        }

        if (daySlots.length > 0) {
            return daySlots.map((slot, index) => (
                <div
                    key={index}
                    className="px-1 pt-2px rounded-2 ftrans-300-eio"
                    onClick={() => handleSlotSelect(slot.start)}
                    style={{
                        backgroundColor:
                            selectedSlot === slot.start
                                ? plt.colors.secondary.main
                                : plt.colors.secondary.lighter,
                        color: selectedSlot === slot.start ? "#fff" : "inherit",
                        cursor: "pointer",
                    }}
                >
                    {moment(slot.start).format("HH:mm")}
                </div>
            ));
        } else {
            return (
                <div className={`pt-2px`} style={{color: plt.text.primary40}}>
                    Нет слотов на выбранный день
                </div>
            );
        }
    };

    return (
        <FC cls={`${className}`}>
            <FRCC cls="week-navigation">
                <ChevronLeftRoundedIcon
                    className={`fs-3 transition-tf-eio transition-d-300 transition-all`}
                    style={{
                        color: plt.text.primary60,
                        width: currentWeekStart.isSame(moment().startOf("isoWeek"), "week") ? "0" : "1em",
                        cursor: currentWeekStart.isSame(moment().startOf("isoWeek"), "week") ? "not-allowed" : "pointer",
                    }}
                    onClick={() => {
                        if (!currentWeekStart.isSame(moment().startOf("isoWeek"), "week")) {
                            setCurrentWeekStart(currentWeekStart.clone().subtract(1, "weeks"));
                        }
                    }}
                />
                {renderDayButtons()}
                <ChevronRightRoundedIcon
                    className={`fs-3`}
                    style={{color: plt.text.primary60, cursor: "pointer"}}
                    onClick={() =>
                        setCurrentWeekStart(currentWeekStart.clone().add(1, "weeks"))
                    }
                />
            </FRCC>
            <FRSC cls="slots" p={1} rounded={3} g={1} wrap
                  bg={plt.colors.secondary.light}>
                {isLoading
                    ? <FCCC grow><CircularProgress size={'30px'}/></FCCC>
                    : renderSlots()}
            </FRSC>
        </FC>
    );
};

export default PsychologistConsultationDateTimePicker;
